import DOMPurify from 'dompurify';

export const DOMPurifyConfig = {
  FORBID_ATTR: ['class', 'data-remote', 'data-url', 'data-type', 'data-method'],
  FORBID_TAGS: ['style', 'mstyle', 'script', 'meta'],
  ADD_ATTR: [],
};

export const PARENTHESES_REGEX = new RegExp(/\(((?:[^)(]+|\([^]*\))*)\)/g);
export const LOCATION_SECTIONS = ['location', 'past_location'];

export const decodeString = (str) => {
  if (!str) return str;
  try {
    return decodeURIComponent(str);
  } catch (e) {
    // If decoding fails, return the original string
    return str;
  }
};

export function getTitleStrings (matches, queries) {
  const icpRequirements = matches?.search_gen_response?.icp_requirements || []
  const queryDict = queries || {} // Assuming queries is a dictionary
  let roles = []

  icpRequirements.forEach(req => {
    if (req.section_name === 'Current Role') {
      let bool_str = req.bool_str

      // Remove brackets from the bool_str
      bool_str = bool_str.replace(/[()]/g, '')

      let ors = bool_str.split('OR')
      ors.forEach(orval => {
        let orval_buckets = orval.split(':')

        if (orval_buckets.length === 2) {
          // If the format is key:value, take the value and process it
          roles.push(orval_buckets[1].toLowerCase().trim())
        } else {
          const key = orval_buckets[0].trim()
          const queryValue = queryDict[key]

          if (queryValue && queryValue.name) {
            // If the key exists in queryDict and has a name field
            roles.push(queryValue.name.toLowerCase())
          } else {
            // Fallback: Push the key itself as a lowercase string
            roles.push(key.toLowerCase())
          }
        }
      })
    }
  })

  // Remove duplicates from roles
  roles = [...new Set(roles)]

  return roles
}

export const BooleanStrTokensByParanthesis = (str) => {
  if (!str || str.length === 0) return [];

  if (str.indexOf('(') === -1) return [str];

  const tokens = [];
  if (str.indexOf('(') === 0 && str.lastIndexOf(')') === str.length - 1 && (str.includes('("(') || str.includes('(('))) {
    str = str.substring(1, str.length - 1);
  }
  for (const match of str.match(PARENTHESES_REGEX) || []) {
    const _no_bracket_match = match.replace(/[()]/g, '');
    tokens.push(_no_bracket_match);
  }
  return tokens;
};

/**
 * Tokenize boolean strings to extract individual terms/values
 * Examples:
 * - (('Python' OR 'PyTorch' OR 'Tensorflow')) => ['Python', 'PyTorch', 'Tensorflow']
 * - (workexp.current.title:Senior Software Engineer OR 5eba5e225a84de00071dd20d) => ['workexp.current.title:Senior Software Engineer', '5eba5e225a84de00071dd20d']
 */
export const tokenizeBoolStr = (boolStr) => {
  if (!boolStr || boolStr.length === 0) return [];

  // Remove outer parentheses if they wrap the entire string
  let cleanStr = boolStr.trim();
  while (cleanStr.startsWith('((') && cleanStr.endsWith('))')) {
    cleanStr = cleanStr.slice(2, -2).trim();
  }
  while (cleanStr.startsWith('(') && cleanStr.endsWith(')')) {
    // Check if the parentheses actually wrap the entire expression
    let parenCount = 0;
    let wrapsEntire = true;
    for (let i = 0; i < cleanStr.length; i++) {
      if (cleanStr[i] === '(') parenCount++;
      if (cleanStr[i] === ')') parenCount--;
      if (parenCount === 0 && i < cleanStr.length - 1) {
        wrapsEntire = false;
        break;
      }
    }
    if (wrapsEntire) {
      cleanStr = cleanStr.slice(1, -1).trim();
    } else {
      break;
    }
  }

  const tokens = [];
  
  // Split by AND/OR operators, but be careful with nested parentheses and quoted strings
  const parts = splitByLogicalOperators(cleanStr);
  
  parts.forEach(part => {
    const trimmed = part.trim();
    if (trimmed) {
      // Remove quotes if present
      if ((trimmed.startsWith("'") && trimmed.endsWith("'")) || 
          (trimmed.startsWith('"') && trimmed.endsWith('"'))) {
        tokens.push(trimmed.slice(1, -1));
      } else {
        tokens.push(trimmed);
      }
    }
  });

  return tokens.filter(token => token.length > 0);
};

/**
 * Split a boolean string by AND/OR operators while respecting parentheses and quotes
 */
const splitByLogicalOperators = (str) => {
  const results = [];
  let current = '';
  let parenLevel = 0;
  let inQuotes = false;
  let quoteChar = '';
  
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const nextChars = str.slice(i, i + 4);
    
    // Handle quotes
    if ((char === '"' || char === "'") && !inQuotes) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (char === quoteChar && inQuotes) {
      inQuotes = false;
      quoteChar = '';
      current += char;
    } else if (inQuotes) {
      current += char;
    } else {
      // Handle parentheses
      if (char === '(') {
        parenLevel++;
        current += char;
      } else if (char === ')') {
        parenLevel--;
        current += char;
      } else if (parenLevel === 0) {
        // Check for OR operator
        if (nextChars === ' OR ' || nextChars.startsWith(' OR')) {
          results.push(current);
          current = '';
          i += 3; // Skip ' OR '
          continue;
        }
        // Check for AND operator  
        else if (nextChars === ' AND' || nextChars.startsWith(' AND')) {
          results.push(current);
          current = '';
          i += 4; // Skip ' AND '
          continue;
        } else {
          current += char;
        }
      } else {
        current += char;
      }
    }
  }
  
  if (current.trim()) {
    results.push(current);
  }
  
  return results;
};

export const buildSummaryMacroInfoV2 = (requirements, macros_by_id_) => {
  const mustHaveStrs = [];
  const niceToHaveStrs = [];
  const locStr = [];
  const NOT_IDENTIFIER = '!';
  if(!requirements) return '';
  // filter section_id === internal
  const sanatizedRequirements = requirements.filter(
    (requirement) => requirement.section_id.indexOf('internal') === -1
  );
  sanatizedRequirements.forEach((requirement) => {
    const { bool_str, section_id, section_type, section_name } = requirement;

    //TODO:- handle roles/must have/ advanced cases spl...
    const _attrs_str = [];
    if (section_id === 'internal') {
      return;
    }
    if (LOCATION_SECTIONS.includes(section_id)) {
      const _attrs = JSON.parse(bool_str);
      _attrs.forEach((attr) => {
        if(!attr || !attr.address) return;
        const address = attr.negate_match
          ? `NOT ${attr.address}`
          : attr.address;
        _attrs_str.push(address);
      });
      locStr.push(
        `Good to have if candidate is from ${section_name} ${_attrs_str.join(' or ')}`
      );
      return;
    } else {
      let _rules = BooleanStrTokensByParanthesis(
        decodeString(bool_str)
      );
      if(requirement.section_id === 'exec_mh' && _rules[0].includes(' AND ')){
        _rules = _rules[0].split(' AND ');
      }

      _rules.forEach((rule) => {
        const operator = rule.indexOf(' AND ') > -1 ? ' AND ' : ' OR ';
        const _attrs = rule.split(operator);
        const _attrs_str = [];
        _attrs.forEach((attr) => {
          let _attr = attr.split(':').splice(-1)[0];
          const is_neg = _attr.startsWith(NOT_IDENTIFIER);
          _attr = _attr.replace(NOT_IDENTIFIER, '');
          if (macros_by_id_[_attr]) {
            const macro = macros_by_id_[_attr];
            let _name = is_neg ? `NOT ${decodeString(macro.description) || macro.name}` : `${decodeString(macro.description) || macro.name}`;
            if (
              macro &&
              macro.auto_gen_cxt &&
              !macro.auto_gen_cxt.declared &&
              macro.auto_gen_cxt.verified
            ) {
              _name += '<sup>*</sup>';
            }
            _attrs_str.push(_name);
          } else {
            _attrs_str.push(is_neg ? `NOT ${_attr}` : `${_attr}`);
          }
        });
        if (section_type === 'Must Have') {
          if (section_name === 'Overall Experience') {
            const [lower_bound, upper_bound] = _attrs_str.join(operator).split('-');
            if (lower_bound && upper_bound) {
              mustHaveStrs.push(
                `Must have Overall Experience no less than ${Number(lower_bound) / 12} years and no greater than ${Number(upper_bound) / 12} years`
              );
            } else {
              mustHaveStrs.push(
                `Ideal to have ${section_name} in any of these values (${_attrs_str.join(operator)})`
              );
            }
          } else {
            mustHaveStrs.push(
              `Ideal to have ${section_name} in any of these values (${_attrs_str.join(operator)})`
            );
          }
        } else {
          niceToHaveStrs.push(
            `Good to have ${section_name} among these values (${_attrs_str.join(operator)})`
          );
        }
      });
    }
  });
  return `${mustHaveStrs.join('. ')}${niceToHaveStrs.join('. ')} ${locStr.join('. ')}`;
};

const parseMacroForName = (macro, macros_by_id_) => {
  if (macro.startsWith('Optional: ')) {
    return macro.split(': ').pop();
  } else {
    return macros_by_id_[macro.split(':').at(0)]?.name || macro.split(':').at(0);
  }
}

export const matchingAttrs = (matchingMacros, icpRequirements, macros_by_id_) => {
  // const section_id: macros by tokenize bool_str 
  const sectionIdToMacros = {};
  icpRequirements.forEach((requirement) => {
    const { section_id, bool_str } = requirement;
    const macros = tokenizeBoolStr(decodeString(bool_str));
    macros.forEach((macro) => {
      sectionIdToMacros[macro] = section_id;
    });
  });
  // Map section_id to their matching attributes
  const matchingAttrs = [];
  matchingMacros.forEach((macro) => {
    const _macro = macro.startsWith('Optional: ') ? macro.replace('Optional: ', '').replace('candidate_sloppy:', '') : macro.split(':').at(0);
    
    const section_id = sectionIdToMacros[_macro];
    if (section_id) {
      matchingAttrs.push({ [section_id]:  parseMacroForName(macro, macros_by_id_) });
    }
  });
  return matchingAttrs;
}

export const sanitizeHTML = (html, allowClass = false, allowLinkTarget = false) => {
  if (allowClass) {
    DOMPurifyConfig.FORBID_ATTR.splice(0, 1);
  }

  if (allowLinkTarget) {
    (DOMPurifyConfig.ADD_ATTR).push('target');
  }

  return DOMPurify.sanitize(html, DOMPurifyConfig)
}

// All functions are now exported individually above
